import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { FaInstagram, FaFacebookF, FaYoutube, FaXTwitter, FaPinterest, FaLinkedinIn } from 'react-icons/fa6';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const data = await api.get('/footer');
        setFooterData(data);
      } catch (err) {
        console.error('Failed to fetch footer:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFooter();
  }, []);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const iconMap = {
    FaInstagram: FaInstagram,
    FaFacebookF: FaFacebookF,
    FaYoutube: FaYoutube,
    FaXTwitter: FaXTwitter,
    FaPinterest: FaPinterest,
    FaLinkedinIn: FaLinkedinIn
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setSubscribing(true);
    try {
      await api.post('/newsletter/subscribe', { email });
      toast.success('Thank you for subscribing!');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) return <div className="text-center py-6 text-gray-500">Loading footer...</div>;
  if (!footerData) return null;

  return (
    <footer className="bg-white w-full pt-12 pb-6 border-t border-gray-100">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 lg:px-16">
        <div className="flex flex-col md:hidden">
          {footerData.columns.map((column) => (
            <div key={column._id} className="border-b border-gray-100 py-4">
              <button
                onClick={() => toggleSection(column.title)}
                className="w-full flex items-center justify-between text-center"
              >
                <span className="text-sm uppercase tracking-[0.12em] text-gray-800 font-medium w-full">
                  {column.title}
                </span>
                <span className="flex-shrink-0">
                  {openSection === column.title ? (
                    <FiChevronUp size={20} className="text-gray-500" />
                  ) : (
                    <FiChevronDown size={20} className="text-gray-500" />
                  )}
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openSection === column.title ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="text-center">
                  {column.items.map((item, idx) => (
                    <Link key={idx} to={item.link} className="text-sm text-gray-700 hover:text-[var(--primary-color)] font-normal block py-1">
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden md:grid grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-8 mb-8">
          {footerData.columns.map((column) => (
            <div key={column._id} className="flex flex-col gap-3">
              <h4 className="uppercase text-sm font-medium tracking-[0.12em] text-gray-800 mb-1">
                {column.title}
              </h4>
              {column.items.map((item, idx) => (
                <Link key={idx} to={item.link} className="text-sm text-gray-700 hover:text-[var(--primary-color)] font-normal">
                  {item.name}
                </Link>
              ))}
            </div>
          ))}

          <div className="flex flex-col gap-3">
            <h4 className="uppercase text-sm font-medium tracking-[0.12em] text-gray-800 mb-1">
              GET IN TOUCH
            </h4>
            <div>
              <p className="font-bold text-sm text-gray-900">Phone:</p>
              <a href={`tel:${footerData.contactInfo?.phone}`} className="text-sm text-gray-700 hover:text-black underline underline-offset-2 font-normal">
                {footerData.contactInfo?.phone || '+92-42-111-178-956'}
              </a>
            </div>
            <div>
              <p className="font-bold text-sm text-gray-900">Email:</p>
              <a href={`mailto:${footerData.contactInfo?.email}`} className="text-sm text-gray-700 hover:text-black underline underline-offset-2 font-normal">
                {footerData.contactInfo?.email || 'care@stylo.pk'}
              </a>
            </div>
            <div>
              <p className="font-bold text-sm text-gray-900">Customer Care Timings:</p>
              <p className="text-sm text-gray-700 font-normal">Monday to Saturday,<br />09:00 AM to 09:00 PM</p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h4 className="uppercase text-sm font-medium tracking-[0.12em] text-gray-800 mb-1">
              SIGN UP AND SAVE
            </h4>
            <p className="text-sm text-gray-700 font-normal leading-relaxed">
              Subscribe to get special offers, free giveaways, and once-in-a-lifetime deals.
            </p>
            <form onSubmit={handleSubscribe} className="relative w-full max-w-[260px] mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-b border-gray-900 py-2 pr-10 text-sm text-gray-700 font-normal focus:outline-none placeholder:text-gray-400 text-center"
                disabled={subscribing}
              />
              <button type="submit" className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-900 hover:text-black" disabled={subscribing}>
                <FiMail size={20} />
              </button>
            </form>
            <div className="flex items-center justify-center gap-4 mt-2">
              {footerData.socialLinks.map((social, idx) => {
                const IconComponent = iconMap[social.icon];
                if (!IconComponent) return null;
                return (
                  <a key={idx} href={social.link} className="text-gray-900 hover:text-[var(--primary-color)] text-2xl">
                    <IconComponent />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="text-center text-sm text-gray-600 font-normal pt-6 border-t border-gray-200">
          <p className="mb-1">© {new Date().getFullYear()} ArshiVastra | All Rights Reserved</p>
          <p className="text-xs text-gray-500">
            Developed by{' '}
            <a 
              href="https://vocadev.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-600 hover:text-[var(--primary-color)] transition-colors"
            >
              vocadev
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;